/**
 * Axios HTTP Client Implementation
 *
 * Implements the IHttpClient port using Axios.
 * Includes interceptors for authentication and error handling.
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import type {
  IHttpClient,
  HttpResponse,
  HttpRequestConfig,
} from '@application/ports';
import { apiConfig } from '@infrastructure/config';

/** Devuelve el access token vigente del SSO, o null si no hay sesion. */
export type TokenProvider = () => string | null;

export class AxiosHttpClient implements IHttpClient {
  private instance: AxiosInstance;

  constructor(private readonly getToken: TokenProvider) {
    this.instance = axios.create({
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout,
      headers: apiConfig.headers,
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // El token nunca se guarda en localStorage: lo administra el servicio de
    // autenticacion, que lo renueva en silencio contra la sesion del SSO.
    this.instance.interceptors.request.use(
      config => {
        const token = this.getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor - Handle errors
    this.instance.interceptors.response.use(
      response => response,
      error => {
        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
          // La sesion caduco o fue revocada: volver a la pantalla del SSO.
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
          console.error('Access forbidden:', error.response.data);
        }

        // Handle 500 Server Error
        if (error.response?.status >= 500) {
          console.error('Server error:', error.response.data);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Transform Axios response to our HttpResponse type
   */
  private transformResponse<T>(response: AxiosResponse<T>): HttpResponse<T> {
    return {
      data: response.data,
      status: response.status,
      headers: response.headers as Record<string, string>,
    };
  }

  /**
   * Transform our HttpRequestConfig to AxiosRequestConfig
   */
  private transformConfig(
    config?: HttpRequestConfig
  ): AxiosRequestConfig | undefined {
    if (!config) return undefined;

    return {
      headers: config.headers,
      params: config.params,
      timeout: config.timeout,
    };
  }

  async get<T>(
    url: string,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.instance.get<T>(
      url,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.instance.post<T>(
      url,
      data,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.instance.patch<T>(
      url,
      data,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.instance.put<T>(
      url,
      data,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  async delete<T>(
    url: string,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.instance.delete<T>(
      url,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }
}
